import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../contextStore/AuthContext';
import { ToastContext } from '../../contextStore/ToastContext';
import { createOffer } from '../../firebase/offers';
import { formatPrice } from '../../utils/formatters';
import { tokenKindForProduct } from '../../utils/tokenConfig';
import ButtonSpinner from '../UI/ButtonSpinner';
import './MakeOfferModal.css';

const QUICK_PICKS = [
  { label: '90%', factor: 0.9 },
  { label: '80%', factor: 0.8 },
  { label: '70%', factor: 0.7 },
  { label: '60%', factor: 0.6 },
];

function MakeOfferModal({ product, onClose, onSuccess }) {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);

  const PAYMENT_OPTIONS = [
    { value: 'cash', label: t('offers.paymentCash'), icon: '€' },
    { value: 'upi', label: t('offers.paymentUpi'), icon: '⚡' },
    { value: 'bank_transfer', label: t('offers.paymentBankTransfer'), icon: '🏦' },
  ];

  const DELIVERY_OPTIONS = [
    { value: 'pickup', label: t('offers.deliveryPickup'), icon: '📍' },
    { value: 'delivery', label: t('offers.deliveryDelivery'), icon: '🚚' },
    { value: 'meet', label: t('offers.deliveryMeet'), icon: '🤝' },
  ];
  const { addToast } = useContext(ToastContext);
  const [offerType, setOfferType] = useState('money');
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [deliveryPreference, setDeliveryPreference] = useState('pickup');
  const [barterProductName, setBarterProductName] = useState('');
  const [barterDescription, setBarterDescription] = useState('');
  const [barterProductImage, setBarterProductImage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(1);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const sliderRef = useRef(null);
  const inputRef = useRef(null);

  const price = product?.price ? Number(product.price) : 0;
  const numAmount = Number(amount) || 0;
  const percentage = price > 0 ? Math.round((numAmount / price) * 100) : 0;
  const savings = price > 0 ? price - numAmount : 0;
  const productImage =
    product?.thumbnailUrl || product?.images?.[0] || product?.url || '';

  // Entrance animation
  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      const timer = setTimeout(() => inputRef.current.focus(), 350);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => onClose(), 280);
  }, [onClose]);

  // Escape key handler
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [handleClose]);

  const getOfferStrength = () => {
    if (percentage >= 90)
      return { label: t('offers.strongOffer'), cls: 'strong', emoji: '💪' };
    if (percentage >= 75)
      return { label: t('offers.goodOffer'), cls: 'good', emoji: '👍' };
    if (percentage >= 60)
      return { label: t('offers.fairOffer'), cls: 'fair', emoji: '🤔' };
    if (percentage > 0)
      return { label: t('offers.lowOffer'), cls: 'low', emoji: '⚠️' };
    return { label: '', cls: '', emoji: '' };
  };

  const handleQuickPick = (factor) => {
    const val = Math.round(price * factor);
    setAmount(String(val));
  };

  const handleSliderChange = (e) => {
    const pct = Number(e.target.value);
    const val = Math.round((pct / 100) * price);
    setAmount(String(val));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      addToast?.(t('auth.loginTitle'), 'error');
      return;
    }
    if (offerType === 'barter') {
      if (!barterProductName.trim()) {
        addToast?.(t('offers.enterBarterItem'), 'error');
        return;
      }
    } else {
      if (!amount || isNaN(Number(amount))) {
        addToast?.(t('offers.enterValidAmount'), 'error');
        return;
      }
    }
    setLoading(true);
    try {
      await createOffer({
        productId: product.id,
        productName: product.name,
        productImage,
        buyerId: user.uid,
        sellerId: product.userId,
        offerAmount: offerType === 'barter' ? 0 : Number(amount),
        originalPrice: price,
        paymentToken: tokenKindForProduct(product),
        message,
        paymentMethod: offerType === 'barter' ? '' : paymentMethod,
        deliveryPreference,
        offerType,
        barterProductName: offerType === 'barter' ? barterProductName : '',
        barterProductImage: offerType === 'barter' ? barterProductImage : '',
        barterDescription: offerType === 'barter' ? barterDescription : '',
      });
      setSuccess(true);
      addToast?.(t('offers.offerSentSuccessfully'), 'success');
      setTimeout(() => {
        onSuccess();
        handleClose();
        setSuccess(false);
        setAmount('');
        setMessage('');
        setBarterProductName('');
        setBarterDescription('');
        setBarterProductImage('');
      }, 2000);
    } catch (err) {
      console.error('MakeOfferModal: failed to create offer', err);
      addToast?.(t('common.error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const strength = getOfferStrength();

  return (
    <div className={`offerModal ${visible ? 'offerModalVisible' : ''}`}>
      <div className="offerModalContent">
        <button className="offerModalClose" onClick={handleClose}>
          ×
        </button>
        <div className="offerModalHeader">
          <h2>{t('offers.makeOffer')}</h2>
          <p className="offerModalSubtitle">
            {t('offers.yourOffer')} ·{' '}
            {strength.label && (
              <span className={`offerStrength ${strength.cls}`}>
                {strength.emoji} {strength.label}
              </span>
            )}
          </p>
        </div>

        {step === 1 && (
          <div className="offerModalStep">
            <div className="offerProductInfo">
              {productImage && (
                <img src={productImage} alt={product.name} className="offerProductImage" />
              )}
              <div className="offerProductDetails">
                <h3>{product.name}</h3>
                <p className="offerPrice">
                  {t('offers.askingPrice')}: <strong>{formatPrice(price, product)}</strong>
                </p>
              </div>
            </div>
            <div className="offerTypeToggle">
              <button
                type="button"
                className={`offerTypeBtn ${offerType === 'money' ? 'offerTypeBtnActive' : ''}`}
                onClick={() => setOfferType('money')}
              >
                💰 {t('offers.moneyOffer')}
              </button>
              <button
                type="button"
                className={`offerTypeBtn ${offerType === 'barter' ? 'offerTypeBtnActive' : ''}`}
                onClick={() => setOfferType('barter')}
              >
                🔄 {t('offers.barterOffer')}
              </button>
            </div>
            {offerType === 'money' ? (
              <div className="offerAmountSection">
                <h3>{t('offers.product')}</h3>
                <div className="offerQuickPicks">
                  {QUICK_PICKS.map((pick) => (
                    <button
                      key={pick.label}
                      type="button"
                      className={`offerQuickPick ${
                        amount === String(Math.round(price * pick.factor)) ? 'offerQuickPickActive' : ''
                      }`}
                      onClick={() => handleQuickPick(pick.factor)}
                    >
                      {pick.label}
                    </button>
                  ))}
                </div>
                <div className="offerSlider">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={percentage}
                    onChange={handleSliderChange}
                    ref={sliderRef}
                  />
                  <div className="offerAmountDisplay">
                    <span className="offerAmountValue">{formatPrice(numAmount, product)}</span>
                    {savings > 0 && (
                      <span className="offerSavings">
                        {t('common.save')} {formatPrice(savings, product)}
                      </span>
                    )}
                  </div>
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={t('offers.yourOffer')}
                  className="offerAmountInput"
                />
              </div>
            ) : (
              <div className="offerBarterSection">
                <h3>{t('offers.barterYourItem')}</h3>
                <input
                  type="text"
                  value={barterProductName}
                  onChange={(e) => setBarterProductName(e.target.value)}
                  placeholder={t('offers.barterItemName')}
                  className="offerAmountInput"
                />
                <input
                  type="text"
                  value={barterProductImage}
                  onChange={(e) => setBarterProductImage(e.target.value)}
                  placeholder={t('offers.barterItemImageUrl')}
                  className="offerAmountInput"
                />
                <textarea
                  value={barterDescription}
                  onChange={(e) => setBarterDescription(e.target.value)}
                  placeholder={t('offers.barterDescription')}
                  className="offerMessage"
                  rows={3}
                />
              </div>
            )}
            <button
              className="offerModalButton"
              onClick={() => setStep(2)}
              disabled={offerType === 'money' ? !amount || loading : !barterProductName.trim() || loading}
            >
              {loading ? <ButtonSpinner /> : t('common.next')}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="offerModalStep">
            <h3>{t('offers.paymentMethod')}</h3>
            <div className="offerOptions">
              {PAYMENT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`offerOption ${
                    paymentMethod === option.value ? 'offerOptionActive' : ''
                  }`}
                  onClick={() => setPaymentMethod(option.value)}
                >
                  <span className="offerOptionIcon">{option.icon}</span>
                  <span className="offerOptionLabel">{option.label}</span>
                </button>
              ))}
            </div>
            <h3>{t('offers.deliveryPreference')}</h3>
            <div className="offerOptions">
              {DELIVERY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`offerOption ${
                    deliveryPreference === option.value ? 'offerOptionActive' : ''
                  }`}
                  onClick={() => setDeliveryPreference(option.value)}
                >
                  <span className="offerOptionIcon">{option.icon}</span>
                  <span className="offerOptionLabel">{option.label}</span>
                </button>
              ))}
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('offers.messagePlaceholder')}
              className="offerMessage"
              rows={3}
            />
            <button
              className="offerModalButton offerModalSubmit"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <ButtonSpinner /> : t('offers.submit')}
            </button>
          </div>
        )}

        {success && (
          <div className="offerModalSuccess">
            <div className="offerModalSuccessIcon">✓</div>
            <h3>{t('offers.offerSentTitle')}</h3>
            <p>
              {offerType === 'barter'
                ? t('offers.barterSentFor', { item: barterProductName, product: product.name })
                : `${formatPrice(numAmount, product)} ${t('offers.offerSentFor')} ${product.name}`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default MakeOfferModal;
